import { type MutableRefObject, useEffect } from 'react';

import { useFlash } from '@cx-modules/hooks';

interface UseConditionalFlashOptions<T> {
  disabled?: boolean;
  ref: MutableRefObject<HTMLElement | null>;
  trigger: T;
}

/**
 * A wrapper around useFlash that can be disabled.
 * When disabled, it prevents all flash animations and CSS property manipulation.
 */
export const useConditionalFlash = <T>({ disabled = false, ref, trigger }: UseConditionalFlashOptions<T>): void => {
  // Always call useFlash to maintain hook order, but with a disabled trigger
  useFlash({
    options: disabled
      ? {
          compare: () => false, // Never trigger animation
          flashingOpacity: 0.4,
          initialOpacity: 0.4,
          opacityCssVar: '--oc-background-opacity',
        }
      : undefined,
    ref,
    trigger: disabled ? null : trigger,
  });

  // Aggressively clean up any CSS properties and prevent animations when disabled
  useEffect(() => {
    if (disabled && ref.current) {
      const el = ref.current;

      // Remove any CSS custom properties that might cause animation
      const cssPropsToRemove = [
        '--oc-background-opacity',
        '--oc-button-opacity',
        '--oc-cell-rgb',
        '--oc-odds-drifting-bg',
        '--oc-odds-shortening-bg',
        '--oc-odds-drifting-rgb',
      ];

      cssPropsToRemove.forEach(prop => {
        el.style.removeProperty(prop);
      });
    }
  }, [disabled, ref, trigger]); // Include trigger to run on every odds change when disabled
};
